import StateObserver, { now, user } from 'src/StateObserver';
import {
  mapFeatureToDialogOrigin,
  openPopupPromise,
} from 'src/lib/popups/popupOpenClose';
import { playMatch2 } from '../minigame/match2-timestep/match2.play';
import {
  getTreasureExpressDifficulty,
  getOngoingTreasureExpressEvent,
  getTreasureExpressSegmentByDifficulty,
  getUnclaimedMilestones,
  isTreasureExpressAvailable,
} from 'src/replicant/treasureExpress/treasureExpress.getters';
import { Feature } from 'src/replicant/analytics';
import { TreasureExpressOriginFeature } from 'src/replicant/treasureExpress/treasureExpress.analytics.schema';
import { track } from 'src/lib/analytics/utils';
import { resolvePromoGroupFeature } from 'src/game/promo/promo.launch';

/**
 * Open the Treasure Express popup
 */
export async function openTreasureExpress(
  originFeature: TreasureExpressOriginFeature,
) {
  let eventId = getOngoingTreasureExpressEvent(user(), now())?.id;

  const unclaimed = getUnclaimedMilestones(user(), now());
  if (unclaimed) {
    eventId = Object.keys(unclaimed)[0];
  }

  // Do nothing if there is no event to show
  if (!eventId) {
    return false;
  }

  const origin = mapFeatureToDialogOrigin(originFeature);

  // Show the info popup if the user has never seen the weekly leaderboard yet
  if (!user().treasureExpress?.lastSeenAt) {
    await openPopupPromise('popupTreasureExpressInfo', { origin });
  }

  // Initialize event if not yet initialized
  if (!user().treasureExpress?.entries[eventId]) {
    await StateObserver.invoke.initializeTreasureExpress();
  }

  // Update the last seen timestamp
  await StateObserver.invoke.updateTreasureExpressLastSeen();

  // Present the popup in a promise
  const play = await openPopupPromise('popupTreasureExpress', {
    eventId,
    origin,
    originFeature,
  });

  // Any display satisfies the recurring promo for the current event.
  await resolvePromoGroupFeature({
    groupId: 'recurringFeatures',
    itemId: 'treasureExpress',
  });

  // Play puzzle if user has chosen to play from the popup
  if (play) playMatch2({ origin });

  // Return the play value
  return play;
}

/**
 * Claim all unclaimed milestones available for the user
 * All milestones are claimed at once in backend, but the UI will show one by one
 */
export async function claimAllTreasureExpressMilestones() {
  const unclaimed = getUnclaimedMilestones(user(), now());
  if (!unclaimed) return;

  const result = await StateObserver.invoke.claimAllTreasureExpressMilestones();

  if (!result || !result.length) return;

  for (const item of result) {
    const chestTypeByMilestone = ['wood', 'silver', 'gold'] as const;
    const entry = user().treasureExpress.entries[item.eventId];

    track('TreasureExpressRewardCollect', {
      ID: item.eventId,
      stage: item.milestone,
      totalTicketsGained: item.tickets,
      segment: getTreasureExpressSegmentByDifficulty(
        getTreasureExpressDifficulty(user(), item.eventId),
      ),
    });

    await openPopupPromise('popupChestRewards', {
      rewards: item.rewards,
      chestType: chestTypeByMilestone[item.milestone - 1] ?? 'gold',
      feature: Feature.TREASURE_EXPRESS._,
      subFeature: Feature.TREASURE_EXPRESS.CHEST_REWARDS,
    });
  }
}

export async function treasureExpressLaunch() {
  const eventId = getOngoingTreasureExpressEvent(user(), now())?.id;
  if (!eventId) return false;

  // Open on launch if claim is available
  const unclaimed = getUnclaimedMilestones(user(), now());
  if (unclaimed) {
    return openTreasureExpress('launch');
  }

  // Open on launch if there is an ongoing event and the user has never seen it
  const event = getOngoingTreasureExpressEvent(user(), now());
  if (event && !user().treasureExpress.entries[event.id]) {
    return openTreasureExpress('launch');
  }

  // Do nothing
  return false;
}

/**
 * Treasure Express Popup sequence that runs after a game has been played
 * @param win
 * @returns
 */
export async function treasureExpressPostGame(hasWon: boolean) {
  // Skip if feature is not available
  if (!isTreasureExpressAvailable(user(), now())) {
    return false;
  }

  // Skip if user has not won the level
  if (!hasWon) {
    return false;
  }

  // Always show if there is any unclaimed milestone
  const unclaimed = getUnclaimedMilestones(user(), now());
  if (unclaimed) {
    return openTreasureExpress('postPlay');
  }

  // Show if there is an ongoing event and the user has never seen it
  const event = getOngoingTreasureExpressEvent(user(), now());
  const hasSeen = user().treasureExpress?.lastSeenAt;
  if (event && !hasSeen) {
    return openTreasureExpress('postPlay');
  }

  // Nothing to see or claim
  return false;
}
